import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { useApp } from '@retainerclub/hooks';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import {
  Card,
  ShowCustomerUsersDetails,
  ShowPatientDetails,
} from '../../../shared';
import ProductionOrderAddressModal from './ProductionOrderAddressModal/ProductionOrderAddressModal';

function ProductionOrderShowCustomersAndPatients({
  account,
  address,
  addressEditable = false,
  className,
  partnerLocation,
  patients,
  productionOrderId,
  refetch = null,
  user,
}) {
  const { isAdmin } = useApp();
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const uniquePatients = useMemo(() => {
    return [...new Set(patients)];
  }, [patients]);

  // Determine which address to display:
  // 1. ProductionOrder address if it has content
  // 2. Fall back to account's default shipping address (matching backend behavior)
  const displayAddress = useMemo(() => {
    // Check if ProductionOrder has a valid address
    if (address && Object.keys(address).length > 0 && address.address) {
      return address;
    }

    // Fall back to account's default shipping address
    if (account?.accountAddresses) {
      const defaultShippingAddress =
        account.accountAddresses.find(
          (addr) => addr.isShipping && addr.isDefault,
        ) || account.accountAddresses.find((addr) => addr.isShipping);

      if (defaultShippingAddress) {
        return {
          address: defaultShippingAddress.address,
          address2: defaultShippingAddress.address2,
          city: defaultShippingAddress.city,
          region: defaultShippingAddress.region,
          postalCode: defaultShippingAddress.postalCode,
          country: defaultShippingAddress.country,
        };
      }
    }

    return null;
  }, [address, account]);

  return (
    <Card
      className={`h-full p-4 ${className}`}
      wrapperClassName="h-full"
      heading="Customer / Patient(s)"
    >
      <ul className="divide-y divide-gray-100">
        {user && <ShowCustomerUsersDetails user={user} showTypeLabel />}
        {uniquePatients.length > 0 &&
          uniquePatients.map((patient) => (
            <ShowPatientDetails
              patient={patient}
              key={patient.id}
              showTypeLabel
            />
          ))}
      </ul>
      {isAdmin && displayAddress && (
        <div className="mt-4 border-gray-100 border-t pt-4">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-black text-sm leading-6">
              Shipping Address
            </p>
            {addressEditable && (
              <Menu as="div" className="relative flex-none">
                <MenuButton className="-m-2.5 block p-2.5 text-black/70 hover:text-black">
                  <span className="sr-only">Open options</span>
                  <EllipsisVerticalIcon
                    className="h-5 w-5"
                    aria-hidden="true"
                  />
                </MenuButton>
                <Transition
                  as="div"
                  className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-black py-2 shadow-lg ring-1 ring-gray-900/5 transition duration-100 ease-out focus:outline-hidden data-closed:scale-95 data-enter:scale-100 data-leave:scale-95 data-closed:opacity-0 data-enter:opacity-100 data-leave:opacity-0"
                >
                  <MenuItems>
                    <MenuItem>
                      <button
                        type="button"
                        onClick={() => setIsAddressModalOpen(true)}
                        className="block w-full px-3 py-1 text-left text-sm text-white leading-6"
                      >
                        Edit
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Transition>
              </Menu>
            )}
          </div>
          <div className="mt-1 flex flex-col items-start gap-x-2 text-black/70 text-xs leading-5">
            {displayAddress.address && <div>{displayAddress.address}</div>}
            {displayAddress.address2 && <div>{displayAddress.address2}</div>}
            <div>
              {displayAddress.city && <span>{displayAddress.city}</span>}
              {displayAddress.city &&
                (displayAddress.state || displayAddress.region) && (
                  <span>, </span>
                )}
              {(displayAddress.state || displayAddress.region) && (
                <span>{displayAddress.state || displayAddress.region}</span>
              )}
              {displayAddress.postalCode && (
                <span> {displayAddress.postalCode}</span>
              )}
            </div>
            {displayAddress.country && (
              <div className="mt-1">{displayAddress.country}</div>
            )}
          </div>
        </div>
      )}
      {isAdmin && productionOrderId && addressEditable && (
        <ProductionOrderAddressModal
          account={account}
          currentAddress={address}
          handleCloseModal={() => setIsAddressModalOpen(false)}
          open={isAddressModalOpen}
          partnerLocation={partnerLocation}
          productionOrderId={productionOrderId}
          refetch={refetch}
        />
      )}
    </Card>
  );
}

ProductionOrderShowCustomersAndPatients.propTypes = {
  account: PropTypes.shape({
    accountAddresses: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        address: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        region: PropTypes.string,
        postalCode: PropTypes.string,
        isShipping: PropTypes.bool,
        isDefault: PropTypes.bool,
      }),
    ),
  }),
  address: PropTypes.shape({
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    region: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
  }),
  addressEditable: PropTypes.bool,
  className: PropTypes.string,
  partnerLocation: PropTypes.shape({
    name: PropTypes.string,
    partnerShippingAddress: PropTypes.shape({
      id: PropTypes.string,
      address: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      region: PropTypes.string,
      postalCode: PropTypes.string,
      country: PropTypes.string,
    }),
  }),
  patients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  productionOrderId: PropTypes.string,
  refetch: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
};

export default ProductionOrderShowCustomersAndPatients;
